import { useQuery } from '@tanstack/react-query'
import { type ComponentProps, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { childrenQuery, householdQuery, userQuery } from '../account/queries'
import { readSection, type Section, writeSection } from '../account/section'
import { messageForError } from '../api/errors'
import { firstName, type User } from '../api/users'
import { loginHref } from '../auth/return-path'
import { session, signOutTo, useSession } from '../state/session'
import { AccountSection } from './account/AccountSection'
import { FamilySection } from './account/FamilySection'
import { Button } from './ui/Button'
import { FormError } from './ui/FormError'

/**
 * The parent's account page: the kids first, since opening Studio for one is
 * what a parent comes here to do, and their own details beneath. Reads and
 * writes go through the API's acting-user model, in which a household owner
 * may act for every student in it, so the portal needs nothing beyond the
 * parent's own session.
 *
 * Nothing here is drafted to browser storage, unlike the checkout: these are
 * children's details, and an edit lives in memory until it is saved.
 */

export interface AccountPortalProps {
  /** Where a signed-out visitor is sent — and returned from. `data-login-url`. */
  loginUrl?: string
  /** Where logging out lands. `data-logout-url`. */
  logoutUrl?: string
  /** The Studio origin that Open Studio hands off to. `data-studio-url`. */
  studioUrl?: string
  /** Where a parent with no kids yet is pointed. `data-checkout-url`. */
  checkoutUrl?: string
}

export function AccountPortal({
  loginUrl = '/login',
  logoutUrl = '/',
  studioUrl = 'https://studio.codeverse.com',
  checkoutUrl = '/checkout',
}: AccountPortalProps) {
  const status = useSession((state) => state.status)
  const userId = useSession((state) => state.userId)

  // Read once at mount, like the login form: logging out from the rail also
  // flips the status, and it navigates on its own — reacting here would race it.
  const arrivedSignedOut = useRef(status !== 'authenticated')
  useEffect(() => {
    if (arrivedSignedOut.current) {
      const here = window.location.pathname + window.location.search
      window.location.replace(loginHref(loginUrl, here))
    }
  }, [loginUrl])

  if (arrivedSignedOut.current || !userId) return null

  return (
    <SignedInPortal
      userId={userId}
      logoutUrl={logoutUrl}
      studioUrl={studioUrl}
      checkoutUrl={checkoutUrl}
    />
  )
}

interface PortalProps {
  logoutUrl: string
  studioUrl: string
  checkoutUrl: string
}

function SignedInPortal({ userId, ...props }: PortalProps & { userId: string }) {
  const user = useQuery(userQuery(userId))

  // The session keeps a render cache of the signed-in user for the nav button,
  // hydrated at login. This is the freshest copy of that record the page will
  // see, so refresh the cache from it — a parent who signed in elsewhere on the
  // domain, or cleared site data, otherwise stays "My Account" in the nav.
  useEffect(() => {
    if (user.data) session.get().setUser(user.data)
  }, [user.data])

  if (user.isPending) {
    return (
      <p className="text-ink-soft" data-testid="portal-loading">
        Loading your account…
      </p>
    )
  }

  if (user.isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <FormError data-testid="portal-error">{messageForError(user.error)}</FormError>
        <Button variant="secondary" onClick={() => user.refetch()} data-testid="portal-retry">
          Try again
        </Button>
      </div>
    )
  }

  // Children can sign in on the marketing site with their username, and they
  // share the cookie domain. Nothing about the family may reach anyone who is
  // not the parent — and there is nothing else on this page for them.
  if (user.data.scope !== 'account') return <ParentsOnly studioUrl={props.studioUrl} />

  return <ParentPortal parent={user.data} {...props} />
}

function ParentsOnly({ studioUrl }: { studioUrl: string }) {
  return (
    <section
      className="mx-auto w-full max-w-xl rounded-card bg-white p-8 text-center shadow-xl"
      data-testid="parents-only"
    >
      <h1 className="text-2xl font-bold text-ink">This page is for parents</h1>
      <p className="mt-2 text-ink-soft">Your profile, avatar and projects all live in Studio.</p>
      <a
        href={studioUrl}
        className="mt-6 inline-flex h-10 items-center rounded-full bg-brand px-4 text-sm font-medium text-white transition outline-none hover:bg-brand-hover focus-visible:ring-2 focus-visible:ring-brand-ring"
      >
        Open Studio
      </a>
    </section>
  )
}

/** The rail's tabs, in order: the kids first, since they are what a parent comes for. */
const TABS: readonly { section: Section; label: string }[] = [
  { section: 'family', label: 'Your Family' },
  { section: 'account', label: 'Your Account' },
]

const ARROW_DELTA: Record<string, 1 | -1> = { ArrowDown: 1, ArrowUp: -1 }

function ParentPortal({
  parent,
  logoutUrl,
  studioUrl,
  checkoutUrl,
}: PortalProps & { parent: User }) {
  const household = useQuery(householdQuery(parent.id))
  const kids = useQuery(childrenQuery(household.data))

  // The open tab: from the address on arrival, and recorded back into it, so
  // a reload or a copied link lands on the same one.
  const [section, setSection] = useState(() => readSection(window.location.search))
  function select(next: Section) {
    setSection(next)
    writeSection(next)
  }

  const id = useId()
  const tabId = (tab: Section) => `${id}-tab-${tab}`
  const panelId = (tab: Section) => `${id}-panel-${tab}`

  // Arrow keys move between tabs and select as they go, so the selected tab
  // is always the one in the Tab order.
  function onTabKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const delta = ARROW_DELTA[event.key]
    if (delta === undefined) return
    event.preventDefault()

    const current = TABS.findIndex((tab) => tab.section === section)
    const index = (current + delta + TABS.length) % TABS.length
    const next = TABS[index]
    if (!next) return
    select(next.section)
    event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[index]?.focus()
  }

  const householdName =
    household.data && household.data.name !== parent.name ? household.data.name : null

  return (
    <div className="mx-auto w-full max-w-5xl">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-ink" data-testid="greeting">
          Hello, {firstName(parent)}
        </h1>
        <p className="mt-2 text-ink-soft" data-testid="portal-intro">
          Manage your family’s account and subscriptions all in one place
        </p>
        {householdName && (
          <p className="mt-1 text-sm text-ink-soft" data-testid="household-name">
            {householdName}
          </p>
        )}
      </header>

      <div className="grid gap-8 md:grid-cols-[14rem_minmax(0,1fr)]">
        {/* Stays below the site's fixed nav while the panel scrolls. self-start
            keeps the rail its own height; stretched to the row, it would have
            nowhere to move. */}
        <div
          className="flex flex-col gap-2 rounded-card bg-white p-2 shadow-xl md:sticky md:top-nav md:self-start md:p-3"
          data-testid="rail"
        >
          <div
            role="tablist"
            aria-label="Account"
            aria-orientation="vertical"
            onKeyDown={onTabKeyDown}
            className="flex flex-col gap-1 border-b border-black/5 pb-2"
          >
            {TABS.map(({ section: tab, label }) => (
              <RailButton
                key={tab}
                role="tab"
                id={tabId(tab)}
                aria-selected={tab === section}
                aria-controls={panelId(tab)}
                tabIndex={tab === section ? 0 : -1}
                onClick={() => select(tab)}
                data-testid={`tab-${tab}`}
              >
                {label}
              </RailButton>
            ))}
          </div>
          <RailButton onClick={() => signOutTo(logoutUrl)} data-testid="nav-logout">
            Log Out
          </RailButton>
        </div>

        {/* Both panels stay mounted, so an edit in progress survives a look at
            the other tab; the one not selected is hidden. */}
        <main>
          <div
            role="tabpanel"
            id={panelId('family')}
            aria-labelledby={tabId('family')}
            hidden={section !== 'family'}
            data-testid="panel-family"
          >
            <FamilySection
              household={household.data}
              kids={kids.data}
              error={household.error ?? kids.error}
              // Retry only the request that failed. While the household is
              // missing the children query is a skipToken with nothing to run,
              // and it fetches on its own once the household arrives.
              retry={() => {
                if (household.isError) void household.refetch()
                else void kids.refetch()
              }}
              studioUrl={studioUrl}
              checkoutUrl={checkoutUrl}
            />
          </div>
          <div
            role="tabpanel"
            id={panelId('account')}
            aria-labelledby={tabId('account')}
            hidden={section !== 'account'}
            data-testid="panel-account"
          >
            <AccountSection parent={parent} />
          </div>
        </main>
      </div>
    </div>
  )
}

type RailButtonProps = Omit<ComponentProps<'button'>, 'className'>

/** A rail entry: a tab, or the log out action beneath them. */
function RailButton({ type = 'button', ...props }: RailButtonProps) {
  return (
    <button
      {...props}
      type={type}
      className="block w-full rounded-field px-4 py-2.5 text-left text-base font-semibold text-ink outline-none transition hover:bg-brand-soft focus-visible:ring-2 focus-visible:ring-brand-ring aria-selected:bg-brand-soft aria-selected:text-brand"
    />
  )
}
